import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Package, FileText, Upload, Plus, X, Eye, EyeOff } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { useToast } from '@/contexts/ToastContext'
import { useAuth } from '@/contexts/AuthContext'
import { API_BASE_URL } from '@/lib/apiClient'
import ReactMarkdown from 'react-markdown'
import { useTranslation } from 'react-i18next'

interface ReleaseRow {
  id: number
  version: string
  installer_file_name: string
  installer_file_size: number
  created_at: string
}

const CHUNK_SIZE = 5 * 1024 * 1024 // 5 MB per chunk

export const ReleasesIndexPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { success, error: showError } = useToast()
  const { session } = useAuth() as any
  const fileInputRef = useRef<HTMLInputElement>(null)

  // List
  const [releases, setReleases] = useState<ReleaseRow[]>([])
  const [loading, setLoading] = useState(true)

  // Upload form
  const [showUpload, setShowUpload] = useState(false)
  const [version, setVersion] = useState('')
  const [notes, setNotes] = useState('')
  const [previewNotes, setPreviewNotes] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)

  const token = session?.access_token ?? (localStorage.getItem('access_token') || '')

  const authHeaders = {
    'Authorization': `Bearer ${token}`,
  }

  const loadReleases = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/releases?orderBy=created_at&orderDir=desc`, {
        headers: authHeaders,
      })
      if (res.ok) {
        const data = await res.json()
        setReleases(Array.isArray(data) ? data : (data.data ?? []))
      }
    } catch (e) {
      console.error('Failed to load releases', e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadReleases() }, [])

  const fmt = (bytes: number) => {
    if (!bytes) return ''
    if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`
    if (bytes >= 1_000) return `${(bytes / 1_000).toFixed(0)} KB`
    return `${bytes} B`
  }

  const uploadChunk = async (chunkBuffer: ArrayBuffer, chunkPath: string): Promise<string> => {
    const form = new FormData()
    // Multer reads req.body in multipart order: subdir must come before "file" or destination() runs without subdir.
    const subdir = chunkPath.includes('/') ? chunkPath.split('/').slice(0, -1).join('/') : undefined
    if (subdir) form.append('subdir', subdir)
    form.append('file', new Blob([chunkBuffer], { type: 'application/octet-stream' }), chunkPath.split('/').pop() ?? 'chunk')

    const res = await fetch(`${API_BASE_URL}/api/files/upload/releases`, {
      method: 'POST',
      headers: authHeaders,
      body: form,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.error ?? `Upload failed (${res.status})`)
    }
    const data = await res.json()
    return data.fullPath as string
  }

  const handleUpload = async () => {
    if (!version.trim()) { showError(t('moc.error'), t('releasesPage.versionRequired')); return }
    if (!file) { showError(t('moc.error'), t('releasesPage.installerRequired')); return }

    setUploading(true)
    setProgress(0)

    try {
      const buffer = await file.arrayBuffer()
      const totalBytes = buffer.byteLength
      const numChunks = Math.ceil(totalBytes / CHUNK_SIZE)
      const isSplit = numChunks > 1

      // Upload chunks to Express file storage
      const chunkPaths: string[] = []
      for (let i = 0; i < numChunks; i++) {
        const start = i * CHUNK_SIZE
        const end = Math.min(start + CHUNK_SIZE, totalBytes)
        const chunkBuffer = buffer.slice(start, end)
        const chunkName = isSplit
          ? `chunk_${String(i).padStart(4, '0')}`
          : file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
        const chunkPath = isSplit ? `${version}/${chunkName}` : `${version}/${chunkName}`

        const fullPath = await uploadChunk(chunkBuffer, chunkPath)
        chunkPaths.push(fullPath)
        setProgress(Math.round(((i + 1) / numChunks) * 80))
      }

      setProgress(85)

      // Insert release record via Express CRUD
      const releaseRes = await fetch(`${API_BASE_URL}/api/releases`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          version: version.trim(),
          release_notes: notes.trim() || null,
          installer_file_name: file.name,
          installer_file_path: chunkPaths[0],
          installer_file_size: totalBytes,
          installer_mime_type: file.type || 'application/octet-stream',
          is_compressed: false,
          original_file_size: totalBytes,
          is_split: isSplit,
          total_chunks: isSplit ? numChunks : null,
        }),
      })

      if (!releaseRes.ok) {
        const err = await releaseRes.json().catch(() => ({}))
        throw new Error(err.error ?? 'Failed to save release record')
      }

      const releaseData = await releaseRes.json()
      setProgress(90)

      // Insert chunk records for split files
      if (isSplit && releaseData?.id) {
        const chunkRows = chunkPaths.map((p, idx) => ({
          release_id: releaseData.id,
          chunk_index: idx,
          chunk_file_path: p,
        }))
        await fetch(`${API_BASE_URL}/api/release_file_chunks`, {
          method: 'POST',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify(chunkRows),
        })
      }

      setProgress(100)
      success(t('moc.success'), t('releasesPage.releaseUploaded', { version }))

      setVersion('')
      setNotes('')
      setFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
      setShowUpload(false)
      setProgress(0)
      loadReleases()
    } catch (err: any) {
      console.error('Upload error:', err)
      showError(t('releasesPage.uploadFailed'), err.message || t('contactsPage.unknown'))
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white">
      <div className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold flex items-center gap-2">
                <Package className="h-5 w-5" />
                {t('releasesPage.platformTitle')}
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {t('releasesPage.platformSubtitle')}
              </p>
            </div>
          </div>
          <Button
            onClick={() => setShowUpload(v => !v)}
            variant={showUpload ? 'outline' : 'default'}
          >
            {showUpload ? <X className="h-4 w-4 mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            {showUpload ? t('common.cancel') : t('releasesPage.newRelease')}
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">

        {/* Upload form */}
        {showUpload && (
          <Card className="bg-white dark:bg-slate-800 border-blue-200 dark:border-blue-700">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Upload className="h-4 w-4" />
                {t('releasesPage.uploadNew')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">
                  {t('releasesPage.version')} <span className="text-red-500">*</span>
                </label>
                <Input value={version} onChange={e => setVersion(e.target.value)} placeholder="e.g. 1.2.3" disabled={uploading} />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                    {t('releasesPage.releaseNotes')} <span className="text-slate-400 font-normal">(Markdown)</span>
                  </label>
                  <button type="button" onClick={() => setPreviewNotes(v => !v)}
                    className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
                    {previewNotes ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    {previewNotes ? t('releasesPage.edit') : t('releasesPage.preview')}
                  </button>
                </div>
                {previewNotes ? (
                  <div className="min-h-[160px] border border-slate-200 dark:border-slate-600 rounded-md p-3 prose prose-slate dark:prose-invert max-w-none text-sm bg-white dark:bg-slate-900">
                    {notes.trim() ? <ReactMarkdown>{notes}</ReactMarkdown> : <span className="text-slate-400">{t('releasesPage.nothingPreview')}</span>}
                  </div>
                ) : (
                  <textarea value={notes} onChange={e => setNotes(e.target.value)}
                    placeholder={"## What's new\n\n- Feature A\n- Bug fix B"}
                    rows={8} disabled={uploading}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400 text-sm font-mono resize-y" />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">
                  {t('releasesPage.installerFile')} <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-3">
                  <Button type="button" variant="outline" size="sm"
                    onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                    <Upload className="h-4 w-4 mr-2" />
                    {file ? t('releasesPage.replaceFile') : t('releasesPage.selectFile')}
                  </Button>
                  {file && (
                    <span className="text-sm text-slate-600 dark:text-slate-400 truncate">
                      {file.name} <span className="text-slate-400">({fmt(file.size)})</span>
                    </span>
                  )}
                  <input ref={fileInputRef} type="file" className="hidden"
                    onChange={e => setFile(e.target.files?.[0] ?? null)} disabled={uploading} />
                </div>
                {file && file.size > CHUNK_SIZE && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    {t('releasesPage.largeFile', { count: Math.ceil(file.size / CHUNK_SIZE) })}
                  </p>
                )}
              </div>

              {uploading && (
                <div>
                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span>{t('releasesPage.uploading')}</span><span>{progress}%</span>
                  </div>
                  <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-600 transition-all duration-200" style={{ width: `${progress}%` }} />
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-2">
                <Button onClick={handleUpload} disabled={uploading || !version.trim() || !file}>
                  {uploading ? t('releasesPage.uploading') : t('releasesPage.uploadRelease')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Releases list */}
        {loading ? (
          <div className="py-16 text-center text-slate-500 dark:text-slate-400">{t('releasesPage.loading')}</div>
        ) : releases.length === 0 ? (
          <div className="py-16 text-center text-slate-500 dark:text-slate-400">
            <Package className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p className="font-medium">{t('releasesPage.empty')}</p>
            <p className="text-sm mt-1">{t('releasesPage.emptyHint')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {releases.map(r => (
              <Card key={r.id} className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <CardContent className="py-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-9 w-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                      <Package className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 dark:text-white">v{r.version}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                        {r.installer_file_name}
                        {r.installer_file_size ? ` · ${fmt(r.installer_file_size)}` : ''}
                        {' · '}{new Date(r.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <Link to={`/release-notes/${r.version}`}
                    className="flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:underline flex-shrink-0">
                    <FileText className="h-4 w-4" />
                    {t('releasesPage.view')}
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
